import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineUsers,
  HiOutlineCheckCircle,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService, userService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

// Synchronous cache loading function
const getCachedTechnicianTeamsData = () => {
  try {
    const cachedTeams = sessionStorage.getItem('technician_teams_data');
    const cachedShifts = sessionStorage.getItem('shifts_data');
    const cachedUsers = sessionStorage.getItem('users_data');
    
    return {
      teams: cachedTeams ? JSON.parse(cachedTeams) : [],
      shifts: cachedShifts ? JSON.parse(cachedShifts) : [],
      users: cachedUsers ? JSON.parse(cachedUsers).filter(u => u.status === 'Active') : [],
      hasAll: !!(cachedTeams && cachedShifts && cachedUsers)
    };
  } catch (e) {
    console.error('Error parsing cached Technician Teams data:', e);
    return { teams: [], shifts: [], users: [], hasAll: false };
  }
};

const TechnicianTeamsPage = () => {
  // Initialize state with cached data if available (synchronous)
  const cachedInitialData = getCachedTechnicianTeamsData();
  const [teams, setTeams] = useState(cachedInitialData.teams);
  const [shifts, setShifts] = useState(cachedInitialData.shifts);
  const [users, setUsers] = useState(cachedInitialData.users);
  const [loading, setLoading] = useState(!cachedInitialData.hasAll);
  const [error, setError] = useState('');
  const [membersError, setMembersError] = useState('');
  const [newMemberUserId, setNewMemberUserId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({
    teamName: '',
    leadUserId: '',
    memberUserIds: [],
    defaultShiftId: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      // Load fresh data in background if we have cached data, otherwise show loading
      const hasCachedData = cachedInitialData.hasAll;
      loadData(!hasCachedData);
    } else {
      setError('Please login to view technician teams');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      const [teamsData, shiftsData, usersData] = await Promise.all([
        masterDataService.getTechnicianTeams(tenantId),
        masterDataService.getShifts(tenantId),
        userService.getAllUsers().catch(() => [])
      ]);
      setTeams(teamsData || []);
      setShifts(shiftsData || []);
      const activeUsers = (usersData || []).filter(u => u.status === 'Active');
      setUsers(activeUsers);
      
      // Cache the data
      sessionStorage.setItem('technician_teams_data', JSON.stringify(teamsData || []));
      sessionStorage.setItem('shifts_data', JSON.stringify(shiftsData || []));
      sessionStorage.setItem('users_data', JSON.stringify(usersData || []));
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading data:', err);
      // Don't clear cached data on error, keep showing it
      if (!cachedInitialData.hasAll) {
        setTeams([]);
        setShifts([]);
        setUsers([]);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredTeams = teams.filter((team) =>
    team.teamName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingTeam(null);
    setFormData({
      teamName: '',
      leadUserId: '',
      memberUserIds: [],
      defaultShiftId: '',
    });
    setFormErrors({});
    setMembersError('');
    setNewMemberUserId('');
    setShowModal(true);
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    let parsedUserIds = [];
    if (Array.isArray(team.memberUserIdsJson)) {
        parsedUserIds = team.memberUserIdsJson;
    } else if (typeof team.memberUserIdsJson === 'string') {
        try {
            parsedUserIds = JSON.parse(team.memberUserIdsJson);
        } catch (e) {
            parsedUserIds = [];
        }
    }

    setFormData({
      teamName: team.teamName || '',
      leadUserId: team.leadUserId?.toString() || '',
      memberUserIds: Array.isArray(team.memberUserIds) ? team.memberUserIds : [],
      defaultShiftId: team.defaultShiftId?.toString() || '',
    });
    setFormErrors({});
    setMembersError('');
    setNewMemberUserId('');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteTechnicianTeam(id);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete technician team. Please try again.');
      console.error('Error deleting technician team:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.teamName.trim()) {
      errors.teamName = 'Team name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const leadUserId = formData.leadUserId ? parseInt(formData.leadUserId) : null;
      const memberUserIds = (formData.memberUserIds || []).filter((x) => Number.isInteger(x) && x > 0);
      if (leadUserId && !memberUserIds.includes(leadUserId)) {
        memberUserIds.push(leadUserId);
      }

      const teamData = {
        tenantId: tenantId,
        teamName: formData.teamName.trim(),
        leadUserId: leadUserId,
        memberUserIds: memberUserIds,
        defaultShiftId: formData.defaultShiftId ? parseInt(formData.defaultShiftId) : null,
      };

      if (editingTeam) {
        await masterDataService.updateTechnicianTeam(editingTeam.teamId, teamData);
      } else {
        await masterDataService.createTechnicianTeam(teamData);
      }

      await loadData();
      setShowModal(false);
      setFormData({ teamName: '', leadUserId: '', memberUserIds: [], defaultShiftId: '' });
      setEditingTeam(null);
      setMembersError('');
      setNewMemberUserId('');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save technician team. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTeam(null);
    setFormData({ teamName: '', leadUserId: '', memberUserIds: [], defaultShiftId: '' });
    setFormErrors({});
    setMembersError('');
    setNewMemberUserId('');
  };

  const getUserLabel = (userId) => {
    if (!userId) return '-';
    const user = users.find(u => u.userId === userId);
    return user ? `${user.fullName} (${user.username})` : userId;
  };

  const getUserName = (userId) => {
    if (!userId) return '';
    const user = users.find(u => u.userId === parseInt(userId));
    return user ? user.username : userId;
  };

  const getShiftName = (shiftId) => {
    if (!shiftId) return '-';
    const shift = shifts.find(s => s.shiftId === shiftId);
    return shift ? `${shift.shiftCode} (${shift.startTime} - ${shift.endTime})` : '-';
  };

  const getMemberRows = () => {
    const leadId = formData.leadUserId ? parseInt(formData.leadUserId) : null;
    return (formData.memberUserIds || [])
      .slice()
      .sort((a, b) => {
        if (leadId && a === leadId) return -1;
        if (leadId && b === leadId) return 1;
        return a - b;
      })
      .map((userId) => ({
        userId,
        isLead: leadId ? userId === leadId : false
      }));
  };

  const handleAddMember = () => {
    setMembersError('');
    if (!newMemberUserId) {
      setMembersError('Please select a user to add.');
      return;
    }
    const uid = parseInt(newMemberUserId, 10);
    if (!uid || uid <= 0) {
      setMembersError('Invalid user selected.');
      return;
    }
    if ((formData.memberUserIds || []).includes(uid)) {
      setMembersError('User already added.');
      return;
    }
    setFormData((prev) => ({ ...prev, memberUserIds: [...(prev.memberUserIds || []), uid] }));
    setNewMemberUserId('');
  };

  const handleRemoveMember = (userId) => {
    const leadId = formData.leadUserId ? parseInt(formData.leadUserId, 10) : null;
    if (leadId && userId === leadId) {
      setMembersError('Lead user cannot be removed from members.');
      return;
    }
    setMembersError('');
    setFormData((prev) => ({ ...prev, memberUserIds: (prev.memberUserIds || []).filter((id) => id !== userId) }));
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading technician teams..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Technician Teams</h1>
          <p>Manage technician teams for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Team
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search teams..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredTeams.length} {filteredTeams.length === 1 ? 'team' : 'teams'}
          </div>
        </div>

        {filteredTeams.length === 0 ? (
          <div className="empty-state">
            <HiOutlineUsers className="empty-icon" />
            <h3>No teams found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first technician team'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Team
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Team Name</th>
                <th>Lead User</th>
                <th>Members</th>
                <th>Default Shift</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTeams.map((team) => (
                <tr key={team.teamId}>
                  <td>{team.teamId}</td>
                  <td className="name-cell">{team.teamName}</td>
                  <td>{team.leadUserId ? getUserLabel(team.leadUserId) : '-'}</td>
                  <td>{team.memberCount ?? (Array.isArray(team.memberUserIds) ? team.memberUserIds.length : 0)}</td>
                  <td>{getShiftName(team.defaultShiftId)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(team)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(team)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingTeam ? 'Edit Technician Team' : 'Add New Technician Team'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="technician-team-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingTeam
                  ? 'Updating...'
                  : 'Creating...'
                : editingTeam
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="technician-team-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="teamName">
                Team Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="teamName"
                value={formData.teamName}
                onChange={(e) =>
                  setFormData({ ...formData, teamName: e.target.value })
                }
                className={formErrors.teamName ? 'error' : ''}
                placeholder="Enter team name"
              />
              {formErrors.teamName && (
                <span className="field-error">{formErrors.teamName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="leadUserId">Lead User</label>
              <select
                id="leadUserId"
                value={formData.leadUserId}
                onChange={(e) =>
                  setFormData((prev) => {
                    const nextLead = e.target.value;
                    const leadId = nextLead ? parseInt(nextLead) : null;
                    const memberIds = Array.isArray(prev.memberUserIds) ? [...prev.memberUserIds] : [];
                    if (leadId && !memberIds.includes(leadId)) memberIds.push(leadId);
                    return { ...prev, leadUserId: nextLead, memberUserIds: memberIds };
                  })
                }
              >
                <option value="">Select Lead (Optional)</option>
                {users.map((u) => (
                  <option key={u.userId} value={u.userId.toString()}>
                    {u.fullName} ({u.username})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="defaultShiftId">Default Shift</label>
              <select
                id="defaultShiftId"
                value={formData.defaultShiftId}
                onChange={(e) =>
                  setFormData({ ...formData, defaultShiftId: e.target.value })
                }
              >
                <option value="">Select Shift (Optional)</option>
                {shifts.map((shift) => (
                  <option key={shift.shiftId} value={shift.shiftId.toString()}>
                    {shift.shiftCode} ({shift.startTime} - {shift.endTime})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Team Members</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {membersError && <div className="error-message">{membersError}</div>}

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.5rem' }}>
                  <select value={newMemberUserId} onChange={(e) => setNewMemberUserId(e.target.value)}>
                    <option value="">Select User</option>
                    {users.map((u) => (
                      <option key={u.userId} value={u.userId.toString()}>
                        {u.fullName} ({u.username})
                      </option>
                    ))}
                  </select>
                  <button type="button" className="btn-primary" onClick={handleAddMember}>
                    <HiOutlineCheckCircle />
                    Add
                  </button>
                </div>

                {getMemberRows().length === 0 ? (
                  <div className="empty-state" style={{ padding: '1rem' }}>
                    <HiOutlineUsers className="empty-icon" />
                    <h3>No members</h3>
                    <p>Select users above and click Add.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Type</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getMemberRows().map((m) => (
                        <tr key={m.userId}>
                          <td>{getUserLabel(m.userId)}</td>
                          <td>{m.isLead ? 'Lead' : 'Member'}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="btn-icon-delete"
                                onClick={() => handleRemoveMember(m.userId)}
                                title={m.isLead ? 'Lead cannot be removed' : 'Remove'}
                                disabled={m.isLead}
                              >
                                <HiOutlineTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.teamId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.teamName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default TechnicianTeamsPage;
